/**
 * Definições das escalas geriátricas estruturadas.
 * Cada entrada descreve o formulário do dialog + como computar +
 * como serializar no payload que a rota /api/calculators/{key} espera.
 *
 * As perguntas seguem os textos clássicos das escalas validadas em PT-BR.
 */

// Sim / não codificado como 1/0 conforme o gabarito Yesavage 1983
export const GDS15_ITEMS = [
    { q: "Está satisfeito com sua vida?",                                 depression: 0 },
    { q: "Abandonou muitos de seus interesses e atividades?",             depression: 1 },
    { q: "Sente que sua vida está vazia?",                                depression: 1 },
    { q: "Sente-se frequentemente aborrecido?",                           depression: 1 },
    { q: "Está de bom humor a maior parte do tempo?",                     depression: 0 },
    { q: "Tem medo que algo de mau vá lhe acontecer?",                    depression: 1 },
    { q: "Sente-se feliz a maior parte do tempo?",                        depression: 0 },
    { q: "Sente-se frequentemente desamparado?",                          depression: 1 },
    { q: "Prefere ficar em casa a sair e fazer coisas novas?",            depression: 1 },
    { q: "Sente que tem mais problemas de memória que a maioria?",        depression: 1 },
    { q: "Acha que é maravilhoso estar vivo?",                            depression: 0 },
    { q: "Sente-se pouco valioso do jeito que está?",                     depression: 1 },
    { q: "Sente-se cheio de energia?",                                    depression: 0 },
    { q: "Sente que a sua situação é sem esperança?",                     depression: 1 },
    { q: "Acha que as outras pessoas estão em situação melhor que você?", depression: 1 },
];

export const PHQ9_ITEMS = [
    "Pouco interesse ou prazer em fazer as coisas",
    "Sentir-se para baixo, deprimido(a) ou sem esperança",
    "Dificuldade em adormecer ou permanecer dormindo, ou dormir demais",
    "Sentir-se cansado(a) ou com pouca energia",
    "Falta de apetite ou comer demais",
    "Sentir-se mal consigo mesmo(a) — ou que é um fracasso",
    "Dificuldade de se concentrar em coisas (ler, ver TV)",
    "Lentidão ou agitação psicomotora perceptível a outros",
    "Pensamentos de que seria melhor estar morto(a) ou de se ferir",
];
export const PHQ9_SCALE = [
    { value: 0, label: "Nunca" },
    { value: 1, label: "Vários dias" },
    { value: 2, label: "Mais da metade dos dias" },
    { value: 3, label: "Quase todos os dias" },
];

// MEEM — 7 subdomínios (Bertolucci/Brucki)
export const MEEM_SECTIONS = [
    { key: "orientacao_tempo",     label: "Orientação temporal",         max: 5, hint: "Ano, estação, mês, dia da semana, dia do mês" },
    { key: "orientacao_espaco",    label: "Orientação espacial",         max: 5, hint: "Estado, cidade, bairro, hospital/clínica, andar" },
    { key: "registro",             label: "Registro (3 palavras)",       max: 3, hint: "Repete carro/vaso/tijolo imediatamente" },
    { key: "atencao_calculo",      label: "Atenção e cálculo",           max: 5, hint: "100 − 7 sucessivo × 5  OU  soletrar MUNDO ao contrário" },
    { key: "memoria_evocacao",     label: "Memória de evocação",         max: 3, hint: "Recorda as 3 palavras após ~5 min" },
    { key: "linguagem",            label: "Linguagem",                   max: 8, hint: "Nomear, repetir frase, comando 3 etapas, ler/escrever, obedecer" },
    { key: "praxia_construcional", label: "Cópia de pentágonos",         max: 1, hint: "Reproduz pentágonos entrelaçados (0/1)" },
];

// MAN-SF (MNA-SF) — 6 itens
export const MAN_ITEMS = [
    {
        key: "apetite",
        label: "Diminuição da ingestão alimentar nos últimos 3 meses",
        options: [
            { value: 0, label: "Diminuição grave" },
            { value: 1, label: "Diminuição moderada" },
            { value: 2, label: "Sem diminuição" },
        ],
    },
    {
        key: "perda_peso",
        label: "Perda de peso involuntária (3 meses)",
        options: [
            { value: 0, label: "Perda > 3 kg" },
            { value: 1, label: "Não sabe" },
            { value: 2, label: "Perda de 1-3 kg" },
            { value: 3, label: "Sem perda" },
        ],
    },
    {
        key: "mobilidade",
        label: "Mobilidade",
        options: [
            { value: 0, label: "Restrito ao leito" },
            { value: 1, label: "Sai da cama, mas não sai de casa" },
            { value: 2, label: "Sai normalmente" },
        ],
    },
    {
        key: "estresse_agudo",
        label: "Estresse psicológico / doença aguda nos últimos 3 meses",
        options: [
            { value: 0, label: "Sim" },
            { value: 2, label: "Não" },
        ],
    },
    {
        key: "neuropsi",
        label: "Problemas neuropsicológicos",
        options: [
            { value: 0, label: "Demência grave ou depressão" },
            { value: 1, label: "Demência leve" },
            { value: 2, label: "Sem problemas" },
        ],
    },
    {
        key: "imc_or_panturrilha",
        label: "IMC (kg/m²) ou circunferência de panturrilha",
        options: [
            { value: 0, label: "IMC < 19  ·  Panturrilha < 31 cm" },
            { value: 1, label: "IMC 19–20.9" },
            { value: 2, label: "IMC 21–22.9" },
            { value: 3, label: "IMC ≥ 23  ·  Panturrilha ≥ 31 cm" },
        ],
    },
];

// Fried — 5 critérios booleanos
export const FRIED_ITEMS = [
    { key: "weight_loss",  label: "Perda de peso involuntária ≥ 4,5 kg (ou ≥5% peso) no último ano" },
    { key: "exhaustion",   label: "Exaustão (CES-D itens 7 ou 20 — ≥2 dias/semana)" },
    { key: "weakness",     label: "Fraqueza (força de preensão no quintil inferior por sexo/IMC)" },
    { key: "slowness",     label: "Lentidão (marcha 4,57m no quintil inferior por sexo/altura)" },
    { key: "low_activity", label: "Baixa atividade física (dispêndio semanal no quintil inferior)" },
];

// CFS — 9 níveis Rockwood
export const CFS_LEVELS = [
    { value: 1, label: "1 — Muito em forma",                     desc: "Robusto, ativo, exercita-se regularmente" },
    { value: 2, label: "2 — Bem",                                desc: "Sem doença ativa, menos ativo que o nível 1" },
    { value: 3, label: "3 — Bem com comorbidades controladas",   desc: "Sintomas bem controlados; caminha sem ajuda" },
    { value: 4, label: "4 — Vulnerável",                         desc: "Não dependente, mas se cansa" },
    { value: 5, label: "5 — Levemente frágil",                   desc: "Dependência leve em AIVDs" },
    { value: 6, label: "6 — Moderadamente frágil",               desc: "Precisa de ajuda em AVDs" },
    { value: 7, label: "7 — Severamente frágil",                 desc: "Completamente dependente para AVDs; estável" },
    { value: 8, label: "8 — Muito severamente frágil",           desc: "Aproximando-se do fim da vida" },
    { value: 9, label: "9 — Terminal",                           desc: "Expectativa de vida < 6 meses" },
];

// Registry consumível pelo componente StructuredScaleField
export const SCALE_REGISTRY = {
    meem: {
        title: "MEEM (Mini-Exame do Estado Mental)",
        calcKey: "mmse",
        buildPayload: (state) => state,
        // state: {orientacao_tempo, ..., schooling_years}
    },
    gds15: {
        title: "GDS-15 (Escala de Depressão Geriátrica)",
        calcKey: "gds15",
        buildPayload: (state) => ({ answers: state.answers || [] }),
    },
    phq9: {
        title: "PHQ-9 (Depressão)",
        calcKey: "phq9",
        buildPayload: (state) => ({ answers: state.answers || [] }),
    },
    man_score: {
        title: "MAN-SF (Mini Avaliação Nutricional)",
        calcKey: "man_sf",
        buildPayload: (state) => state,
    },
    fried: {
        title: "Fried (Fragilidade)",
        calcKey: "fried",
        buildPayload: (state) => ({
            weight_loss: !!state.weight_loss,
            exhaustion: !!state.exhaustion,
            weakness: !!state.weakness,
            slowness: !!state.slowness,
            low_activity: !!state.low_activity,
        }),
    },
    cfs: {
        title: "CFS (Clinical Frailty Scale — Rockwood)",
        calcKey: "cfs",
        buildPayload: (state) => ({ cfs_level: state.cfs_level }),
    },
};
